import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Database,
  FileText,
  Shield,
  Coins,
  Sparkles,
  ArrowRight,
  CheckCircle,
  BarChart3,
  Target,
  Workflow,
  Layers,
} from 'lucide-react';

const solutionsData = {
  'data-engineering': {
    icon: Database,
    title: 'Sustainability Data Engineering',
    subtitle: 'Build audit-ready data infrastructure for your sustainability program.',
    description: 'Enterprise-grade data pipelines, quality rules, and integration capabilities for comprehensive ESG data management.',
    painPoints: [
      'Fragmented data across multiple systems and formats',
      'Manual data collection and processing errors',
      'Lack of data lineage and audit trails',
      'Inconsistent data quality and validation',
      'Siloed sustainability data from operational systems',
    ],
    deliverables: [
      'Automated data pipelines with real-time ingestion',
      'Data lake/warehouse architecture for ESG data',
      'Quality rules and anomaly detection',
      'Metadata management and data lineage',
      'ESG data integration from 100+ sources',
    ],
    steps: [
      { title: 'Assessment', description: 'Evaluate existing data landscape and requirements' },
      { title: 'Architecture', description: 'Design scalable data infrastructure' },
      { title: 'Implementation', description: 'Build and deploy data pipelines' },
      { title: 'Optimization', description: 'Continuous improvement and monitoring' },
    ],
    outcomes: [
      { value: '99.9%', label: 'Data Accuracy' },
      { value: '60%', label: 'Time Reduction' },
      { value: '100+', label: 'Data Sources' },
      { value: '24/7', label: 'Monitoring' },
    ],
  },
  'reporting': {
    icon: FileText,
    title: 'Sustainability Reporting & Assurance',
    subtitle: 'Comprehensive compliance reporting with audit-ready evidence.',
    description: 'Automated reporting solutions covering all major frameworks with built-in assurance readiness.',
    painPoints: [
      'Manual report generation is time-consuming',
      'Difficulty meeting multiple framework requirements',
      'Lack of audit trails and evidence documentation',
      'Inconsistent reporting across locations',
      'Gap between sustainability and finance teams',
    ],
    deliverables: [
      'GHG inventory automation and Scope 1/2/3 calculations',
      'Multi-framework reporting (ISSB/IFRS, GRI, TCFD, CDP)',
      'Double materiality assessments',
      'Audit-ready evidence packs',
      'Automated disclosure generation',
    ],
    steps: [
      { title: 'Framework Mapping', description: 'Identify applicable standards and requirements' },
      { title: 'Data Alignment', description: 'Map data to reporting requirements' },
      { title: 'Automation', description: 'Build automated reporting workflows' },
      { title: 'Assurance', description: 'Prepare for external verification' },
    ],
    outcomes: [
      { value: '80%', label: 'Faster Reporting' },
      { value: '5+', label: 'Frameworks Covered' },
      { value: '100%', label: 'Audit Ready' },
      { value: '50%', label: 'Cost Reduction' },
    ],
  },
  'dmrv': {
    icon: Shield,
    title: 'dMRV Turnkey Solution',
    subtitle: 'Digital MRV framework for verifiable sustainability claims.',
    description: 'Complete digital Measurement, Reporting, and Verification solution with evidence-based credibility.',
    painPoints: [
      'Manual verification processes are slow and costly',
      'Difficulty proving environmental impact claims',
      'Lack of standardized evidence frameworks',
      'Challenges in real-time project monitoring',
      'Limited interoperability with registries',
    ],
    deliverables: [
      'Comprehensive MRV framework design',
      'Evidence capture and workflow automation',
      'Verification-ready data rooms',
      'Geospatial integration capabilities',
      'Methodology mapping to major standards',
    ],
    steps: [
      { title: 'Framework Design', description: 'Custom MRV methodology development' },
      { title: 'Data Integration', description: 'Connect evidence sources and sensors' },
      { title: 'Workflow Setup', description: 'Automate capture and verification' },
      { title: 'Registry Connect', description: 'Enable credit issuance pipeline' },
    ],
    outcomes: [
      { value: '10x', label: 'Faster Verification' },
      { value: '95%', label: 'Evidence Capture' },
      { value: 'Real-time', label: 'Monitoring' },
      { value: '100%', label: 'Transparency' },
    ],
  },
  'tokenization': {
    icon: Coins,
    title: 'Tokenization & Blockchain',
    subtitle: 'Infrastructure for tokenized environmental assets.',
    description: 'End-to-end tokenization platform for carbon credits and environmental assets with full traceability.',
    painPoints: [
      'Double counting risks in credit markets',
      'Lack of transparency in credit origins',
      'Complex registry and marketplace integration',
      'Limited liquidity for environmental assets',
      'Manual retirement tracking processes',
    ],
    deliverables: [
      'Tokenized credit issuance platform',
      'Digital registry with proof-of-origin',
      'Anti-double counting mechanisms',
      'Automated retirement tracking',
      'Wallet design and management',
    ],
    steps: [
      { title: 'Asset Design', description: 'Define tokenization parameters' },
      { title: 'Infrastructure', description: 'Deploy blockchain infrastructure' },
      { title: 'Integration', description: 'Connect registries and marketplaces' },
      { title: 'Launch', description: 'Enable trading and retirement' },
    ],
    outcomes: [
      { value: '100%', label: 'Traceability' },
      { value: '0', label: 'Double Counting' },
      { value: '24/7', label: 'Trading' },
      { value: 'Instant', label: 'Settlement' },
    ],
  },
  'credibility-engine': {
    icon: Sparkles,
    title: 'Credibility Engine',
    subtitle: 'Finance-grade credibility for sustainability claims.',
    description: 'AI-powered credibility scoring and fraud detection for investor-ready sustainability data.',
    painPoints: [
      'Investor skepticism about sustainability claims',
      'Greenwashing risks and reputational damage',
      'Lack of standardized credibility metrics',
      'Manual due diligence processes',
      'Limited integration with finance workflows',
    ],
    deliverables: [
      'Credibility scoring algorithms',
      'Fraud-risk detection systems',
      'Finance-grade data formatting',
      'Marketplace and exchange integration',
      'Investor reporting dashboards',
    ],
    steps: [
      { title: 'Assessment', description: 'Evaluate data quality and gaps' },
      { title: 'Scoring', description: 'Implement credibility metrics' },
      { title: 'Integration', description: 'Connect to finance systems' },
      { title: 'Reporting', description: 'Generate investor-ready outputs' },
    ],
    outcomes: [
      { value: 'AAA', label: 'Credibility Rating' },
      { value: '99%', label: 'Fraud Detection' },
      { value: 'Real-time', label: 'Scoring' },
      { value: '3x', label: 'Investor Confidence' },
    ],
  },
};

export const Solutions = ({ type = 'data-engineering' }) => {
  const solution = solutionsData[type] || solutionsData['data-engineering'];
  const Icon = solution.icon;

  return (
    <div className="flex flex-col">
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-subtle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 rounded-xl bg-teal/10 text-teal flex items-center justify-center">
                <Icon className="h-6 w-6" />
              </div>
              <Badge variant="outline">Solutions</Badge>
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold text-foreground mb-6">
              {solution.title}
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              {solution.subtitle}
            </p>
            <p className="text-lg text-muted-foreground mb-8">
              {solution.description}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/book-call">
                <Button variant="teal" size="lg" className="group">
                  Book a Discovery Call
                  <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" size="lg">
                  Request a Proposal
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Pain Points */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <Badge variant="outline" className="mb-4">The Challenge</Badge>
              <h2 className="text-3xl font-bold text-foreground mb-6">
                Common Pain Points
              </h2>
              <p className="text-lg text-muted-foreground">
                Organizations face significant challenges in building robust sustainability
                data infrastructure.
              </p>
            </div>
            <div className="space-y-4">
              {solution.painPoints.map((point, index) => (
                <div
                  key={index}
                  className="flex items-start gap-3 p-4 rounded-lg bg-destructive/5 border border-destructive/10"
                >
                  <div className="w-6 h-6 rounded-full bg-destructive/10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-medium text-destructive">{index + 1}</span>
                  </div>
                  <p className="text-foreground">{point}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What We Deliver */}
      <section className="py-20 bg-muted/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge variant="outline" className="mb-4">Deliverables</Badge>
            <h2 className="text-3xl font-bold text-foreground mb-4">
              What We Deliver
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {solution.deliverables.map((item, index) => (
              <Card key={index} className="border-border/50 bg-card">
                <CardContent className="pt-6">
                  <div className="flex items-start gap-3">
                    <CheckCircle className="h-6 w-6 text-teal flex-shrink-0 mt-0.5" />
                    <p className="text-foreground">{item}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <Badge variant="outline" className="mb-4">Process</Badge>
            <h2 className="text-3xl font-bold text-foreground mb-4">
              How It Works
            </h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {solution.steps.map((step, index) => (
              <div key={index} className="relative">
                <div className="flex flex-col items-center text-center">
                  <div className="w-14 h-14 rounded-full bg-teal/10 text-teal flex items-center justify-center mb-4 font-bold text-lg">
                    {index + 1}
                  </div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">{step.title}</h3>
                  <p className="text-sm text-muted-foreground">{step.description}</p>
                </div>
                {index < solution.steps.length - 1 && (
                  <div className="hidden lg:block absolute top-7 left-full w-full h-0.5 bg-border -translate-x-7" />
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Outcomes */}
      <section className="py-20 bg-primary">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-primary-foreground mb-4">
              Expected Outcomes
            </h2>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {solution.outcomes.map((outcome, index) => (
              <div key={index} className="text-center">
                <p className="text-4xl sm:text-5xl font-bold text-teal mb-2">{outcome.value}</p>
                <p className="text-primary-foreground/80">{outcome.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-background">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-muted/50 border-border">
            <CardContent className="p-8 lg:p-12">
              <div className="flex flex-col lg:flex-row items-center justify-between gap-8">
                <div>
                  <h2 className="text-2xl sm:text-3xl font-bold text-foreground mb-3">
                    Ready to Get Started?
                  </h2>
                  <p className="text-lg text-muted-foreground">
                    Schedule a discovery call to discuss your specific requirements.
                  </p>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link to="/book-call">
                    <Button variant="teal" size="lg" className="group">
                      Book a Discovery Call
                      <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button variant="outline" size="lg">
                      Contact Sales
                    </Button>
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default Solutions;
